import React, { FC, useEffect } from 'react';
import { connect } from 'react-redux';
import { useLeaflet } from 'react-leaflet';

import { Dispatch, iRootState } from '../../../store';

const mapState = (state: iRootState) => ({
  fit: state.infrastructureMap.fit
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    setFocus: d.infrastructureMap.setFocus,
    clearFit: d.infrastructureMap.clearFit
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

type IProps = connectedProps;

// Применяет разовый запрос подгонки обзора (state.infrastructureMap.fit) к карте.
// Ничего не рисует. Запрос, пришедший, пока карта не смонтирована (например,
// авто-переход из уведомления), применится здесь при монтировании. Однократность
// применения обеспечивает clearFit — после него fit === null, и эффект не сработает.
const MapFitter: FC<IProps> = ({ fit, setFocus, clearFit }) => {
  const { map } = useLeaflet();

  useEffect(() => {
    if (!map || !fit) {
      return;
    }

    const apply = () => {
      // animate:false — применение мгновенное и не порождает анимацию, которая
      // могла бы «поглотить» следующий fit (та же ловушка, что и ниже)
      map.fitBounds(fit.bounds, {
        padding: [150, 150],
        maxZoom: 22,
        animate: false
      });

      setFocus({
        zoom: map.getZoom(),
        center: [map.getCenter().lat, map.getCenter().lng]
      });
      clearFit();
    };

    // Карта без размеров (контейнер скрыт или ещё не отложен) — fitBounds
    // посчитал бы мусорный обзор; дождёмся первого resize и не сбрасываем запрос
    const size = map.getSize();
    if (!size || size.x === 0 || size.y === 0) {
      map.once('resize', apply);

      return () => {
        map.off('resize', apply);
      };
    }

    // Идущая анимация зума (например, подгонка всего плана в ImageOverlay при
    // смене карты) молча глотает fitBounds — Leaflet._tryAnimatedZoom в этом
    // случае просто возвращает true. Публичного API для проверки нет, читаем
    // приватный флаг и применяем запрос по завершении анимации (moveend).
    if ((map as any)._animatingZoom) {
      map.once('moveend', apply);

      return () => {
        map.off('moveend', apply);
      };
    }

    apply();
  }, [map, fit, setFocus, clearFit]);

  return null;
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(MapFitter);
